<template>
  <PillTabs
    v-if="showTabs"
    v-model="selectedBillingCycle"
    :tabs="tabs"
    class="mx-auto w-[330px] mb-[33px] tablet-h:mb-[64px]"
  />
  <Skeleton
    v-else-if="!tabs.length"
    class="mx-auto mt-[74px] mb-[64px] w-[330px] h-[32px] rounded-[16px]"
  />

  <div class="max-w-[1342px] mx-auto">
    <template v-if="plans">
      <div
        class="flex flex-col flex-wrap tablet-h:flex-row gap-[18px] justify-center"
      >
        <PlanCard
          v-for="plan in filteredPlans"
          :key="plan.id"
          :plan="plan"
          :type="props.type"
          @selected="handleSelectedPlan"
        />
      </div>

      <div
        class="mx-auto mt-[64px] max-w-[490px] text-center text-[18px] leading-[26px] text-gray-800 font-secondary tablet-h:max-w-full"
      >
        Questions about enterprise security, procurement, or custom contracts?
        <a class="text-link text-[18px]" href="mailto:support@dacr.com">
          Contact Sales
        </a>
      </div>
    </template>

    <div
      v-else
      class="flex flex-col tablet-h:flex-row flex-wrap gap-[18px] justify-center"
    >
      <div
        v-for="n in 2"
        :key="n"
        class="flex flex-col tablet-h:w-[380px] h-[666px] rounded-[30px] pt-10 pb-[26px] px-[30px] border-10 border-gray-200/20"
      >
        <Skeleton class="w-[208px] h-4 mb-6" />
        <Skeleton class="w-[130px] h-[25px] mb-12" />
        <Skeleton class="w-[250px] h-4 mb-2" />
        <Skeleton class="w-[208px] h-4" />

        <div class="mt-auto mb-[30px]">
          <Skeleton class="w-full h-[13px] mb-5" />
          <Skeleton class="w-full h-[13px] mb-5" />
          <Skeleton class="w-full h-[13px] mb-5" />
          <Skeleton class="w-full h-[13px] mb-5" />
          <Skeleton class="w-full h-[13px]" />
        </div>

        <Skeleton class="w-full h-[48px] rounded-sm" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import PlanCard from '@/features/billing/components/PlanCard.vue'
import {
  usePlansQueryQuery,
  type Plan,
  type PlanItem,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { SelectPlanType } from '@/shared/types/index'
import { getTokens } from '@dacr/auth'
import { PillTabs, Skeleton } from '@dacr/ui'
import { computed, ref, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

const props = defineProps<{
  type: SelectPlanType
}>()

const router = useRouter()
const dialog = useDialog()
const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const { result } = usePlansQueryQuery(
  { accessToken: getTokens().accessToken },
  { enabled: !webviewStore.isActive }
)

const plans = computed(() => result.value?.plans || billingStore.webviewPlans)

const selectedBillingCycle = ref('')

watchEffect(() => {
  const allBillingCycleGroups = plans.value?.groupByBillingCycle.map(
    (group) => group.billingCycleType
  )
  const isActiveBillingCycleInGroup = allBillingCycleGroups?.includes(
    plans.value?.activeBillingCycleType || ''
  )

  selectedBillingCycle.value =
    (isActiveBillingCycleInGroup
      ? plans.value?.activeBillingCycleType
      : plans.value?.defaultBillingCycleType) || 'year'
})

const noBillingCyclePlans = computed(
  () =>
    plans.value?.groupByBillingCycle.find(
      (billingCycle) => billingCycle?.billingCycleType === 'none'
    )?.plans || []
)

const showNoBillingCyclePlans = computed(
  () =>
    props.type === SelectPlanType.Register && noBillingCyclePlans.value.length
)

const selectedBillingCyclePlans = computed(
  () =>
    plans.value?.groupByBillingCycle.find(
      (billingCycle) =>
        billingCycle?.billingCycleType === selectedBillingCycle.value
    )?.plans || []
)

const filteredPlans = computed(() =>
  showNoBillingCyclePlans.value
    ? [...noBillingCyclePlans.value, ...selectedBillingCyclePlans.value]
    : selectedBillingCyclePlans.value
)

const tabs = computed(
  () =>
    plans.value?.groupByBillingCycle
      .filter((billingCycle) => billingCycle.billingCycleDescription)
      .map((billingCycle) => ({
        label: billingCycle.billingCycleDescription!,
        value: billingCycle.billingCycleType,
        callout: billingCycle.billingCycleCallout,
      })) || []
)

const showTabs = computed(() => (tabs.value?.length || 0) > 1)

const handleSelectedPlan = async (plan: Plan, planItem: PlanItem) => {
  if (webviewStore.isActive) {
    return webviewStore.postMessage({
      type: NativeActionsEnum.PlanSelected,
      data: {
        productId: planItem.nativeProductId,
        planId: planItem.nativePlanId,
      },
    })
  }

  if (
    props.type !== SelectPlanType.Register &&
    !billingStore.canUpdateSubscription
  ) {
    return dialog.open(Dialogs.IAP, { props: { type: 'change' } })
  }

  if (
    props.type === SelectPlanType.Change &&
    planItem.productId === billingStore.billing?.plan.id &&
    plan.items.length === 1
  ) {
    return router.push('/settings/billing')
  }

  const isSubscribedToBundlePlan = plan.items.some(
    (item) => billingStore.billing?.plan.id === item.productId
  )

  if (
    props.type === SelectPlanType.Change &&
    isSubscribedToBundlePlan &&
    plan.items.length > 1
  ) {
    return dialog.open(Dialogs.UpgradeBundleLimit)
  }

  billingStore.setPlanData({
    plan,
    planItem,
    billingCycle: {
      type: selectedBillingCycle.value,
      description:
        plans.value?.groupByBillingCycle.find(
          (billingCycle) =>
            billingCycle?.billingCycleType === selectedBillingCycle.value
        )?.billingCycleDescription || '',
    },
  })

  if (props.type === SelectPlanType.Register) {
    userStore.updateUser({ totalFiles: 0 })
  }

  const isFreePlan = planItem.prices.every((price) => !price.details?.amount)
  if (isFreePlan) {
    return router.push('/home')
  }

  if (props.type === SelectPlanType.Change) {
    return router.push('/change-plans/confirm')
  }

  router.push(`/${props.type.toLowerCase()}/checkout`)
}
</script>
